import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Shield, X } from "lucide-react";

const DETAIL_PLACEHOLDER = {
  mental_emotional: "Only if you want to: how were you feeling?",
  other: "A few words, if you like",
};
const NEEDS_DETAIL = new Set(["other", "mental_emotional"]);

function ReasonGrid({ reasons, value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {reasons.map((r) => (
        <button key={r.key} type="button" data-testid={`reflection-reason-${r.key}`} onClick={() => onChange(r.key)}
          className={`text-left px-3 py-2 rounded-xl text-xs font-medium border transition ${value === r.key ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700 hover:border-amber-400"}`}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

function ShieldOption({ shields, reflection, checked, onChange }) {
  if (!(shields > 0)) return null;
  return (
    <label data-testid="reflection-shield" className={`flex items-start gap-3 rounded-xl p-3 border cursor-pointer transition ${checked ? "bg-emerald-50 border-emerald-300" : "bg-stone-50 border-stone-200 hover:border-emerald-300"}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-emerald-700 w-4 h-4" />
      <span className="flex items-center gap-2 text-xs text-stone-800">
        <Shield className="w-4 h-4 text-emerald-700 shrink-0" />
        <span><strong>Use a Streak Shield</strong> ({shields} left) — keep the goal at {reflection.goal_label.toLowerCase()} and count that week as met.</span>
      </span>
    </label>
  );
}

function ReflectionForm({ reflection, reasons, shields, onSubmit, onDismiss }) {
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState("");
  const [useShield, setUseShield] = useState(false);
  const [busy, setBusy] = useState(false);
  const canSubmit = !!reason && (!NEEDS_DETAIL.has(reason) || detail.trim().length > 0) && !busy;
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(reflection.id, reason, detail, useShield); }
    catch { onDismiss?.(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 pt-3" data-testid="reflection-form">
      <p className="text-xs text-stone-600">
        {reflection.count} of {reflection.goal} that week{reflection.dropped && <> — the goal is now <strong>{reflection.new_goal_label.toLowerCase()}</strong> so it fits again</>}.
        Pick whatever feels closest, or skip this entirely.
      </p>
      <ReasonGrid reasons={reasons} value={reason} onChange={setReason} />
      {reason && (
        <textarea data-testid="reflection-detail" value={detail} onChange={(e) => setDetail(e.target.value)} rows={2}
          placeholder={DETAIL_PLACEHOLDER[reason] || "Anything worth noting? (optional)"}
          className="w-full px-3 py-2 rounded-xl border border-stone-300 text-xs focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none" />
      )}
      <ShieldOption shields={shields} reflection={reflection} checked={useShield} onChange={setUseShield} />
      <button data-testid="reflection-submit" disabled={!canSubmit} onClick={submit} className="amber-btn w-full text-xs disabled:opacity-40">
        {busy ? "Saving…" : useShield ? "Save & use shield (+15 pts)" : "Save note (+15 pts)"}
      </button>
    </div>
  );
}

export function ReflectionInvite({ reflection, reasons, shields = 0, onSubmit, onDismiss }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3" data-testid={`reflection-invite-${reflection.id}`}>
      <div className="flex items-start gap-2">
        <button type="button" data-testid="reflection-toggle" onClick={() => setOpen((v) => !v)} className="flex-1 text-left flex items-start gap-2">
          <ChevronDown className={`w-4 h-4 mt-0.5 text-stone-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="text-sm text-stone-700">
            Want to jot down what got in the way of <strong>{reflection.habit_title}</strong> ({reflection.week})? <span className="text-stone-500">Optional · +15 pts</span>
          </span>
        </button>
        <button type="button" data-testid="reflection-dismiss" onClick={onDismiss} aria-label="Not now" className="p-1 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-200 transition">
          <X className="w-4 h-4" />
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <ReflectionForm reflection={reflection} reasons={reasons} shields={shields} onSubmit={onSubmit} onDismiss={onDismiss} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
